import { useEffect, useRef, useState } from 'react';
import API from '../API';

export type DisconnectReason = 'venue_closed' | 'server_gone';

export interface ServerAliveState {
  disconnected: boolean;
  reason: DisconnectReason | null;
}

interface AliveResponse {
  status: 'ready' | 'gone' | 'closed' | 'starting';
  alive: boolean;
  message?: string;
}

const DEFAULT_POLL_MS = 5000;
const DEFAULT_DEBOUNCE = 2;

/**
 * Polls GET /vv/events/{id}/connect/alive while the user is in-world.
 * Read-only — does not trigger server provisioning.
 */
export function useServerAlive(
  accessToken: string | null,
  eventId: string,
  enabled: boolean,
  options?: { pollIntervalMs?: number; debounceFailures?: number }
): ServerAliveState {
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_MS;
  const debounceFailures = options?.debounceFailures ?? DEFAULT_DEBOUNCE;

  const [state, setState] = useState<ServerAliveState>({
    disconnected: false,
    reason: null,
  });
  const failureCountRef = useRef(0);

  useEffect(() => {
    if (!enabled || !accessToken || !eventId) {
      failureCountRef.current = 0;
      setState({ disconnected: false, reason: null });
      return;
    }

    let cancelled = false;

    const checkAlive = async () => {
      try {
        const response = await API.connectEventAlive(accessToken, { eventId });
        if (cancelled) return;

        if (!response.ok) {
          failureCountRef.current += 1;
          if (failureCountRef.current >= debounceFailures) {
            setState({ disconnected: true, reason: 'server_gone' });
          }
          return;
        }

        const data = (await response.json()) as AliveResponse;

        if (data.status === 'closed') {
          setState({ disconnected: true, reason: 'venue_closed' });
          return;
        }

        if (!data.alive || data.status === 'gone') {
          failureCountRef.current += 1;
          if (failureCountRef.current >= debounceFailures) {
            setState({ disconnected: true, reason: 'server_gone' });
          }
          return;
        }

        failureCountRef.current = 0;
        setState({ disconnected: false, reason: null });
      } catch {
        if (cancelled) return;
        failureCountRef.current += 1;
        if (failureCountRef.current >= debounceFailures) {
          setState({ disconnected: true, reason: 'server_gone' });
        }
      }
    };

    checkAlive();
    const interval = setInterval(checkAlive, pollIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [accessToken, debounceFailures, enabled, eventId, pollIntervalMs]);

  return state;
}
